import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { BlockButton } from '@/components/BlockButton';
import './CalendarDatetimeMenu.scss';
import clsx from 'clsx';

type RenderCustomHeaderProps = {
  date: Date;
  customHeaderCount: number;
  decreaseMonth: () => void;
  increaseMonth: () => void;
};

const renderCustomHeader = ({
  date,
  customHeaderCount,
  decreaseMonth,
  increaseMonth,
}: RenderCustomHeaderProps) => {
  const adjustedDate = new Date(date);
  adjustedDate.setMonth(adjustedDate.getMonth() + customHeaderCount);

  const year = new Intl.DateTimeFormat('ja', { year: 'numeric' }).format(adjustedDate);
  const month = new Intl.DateTimeFormat('ja', { month: 'long' }).format(adjustedDate);

  return (
    <div className="flex justify-between px-2">
      <button
        type="button"
        className="size-lg"
        onClick={(e) => {
          e.stopPropagation();
          decreaseMonth();
        }}
      >
        <img
          src="/agent/job_change_processes/icon-chevron-left.svg"
          alt=""
          width={24}
          height={24}
        />
      </button>
      <span className="text-base font-bold text-sub">{` ${year}${month} `}</span>
      <button
        type="button"
        className="size-lg"
        onClick={(e) => {
          e.stopPropagation();
          increaseMonth();
        }}
      >
        <img
          src="/agent/job_change_processes/icon-chevron-right.svg"
          alt=""
          width={24}
          height={24}
        />
      </button>
    </div>
  );
};

type Props = {
  currentDate: Date | undefined;
  onCloseDateMenu: () => void;
  onSetDate: (date: Date) => void;
  onUpdate?: (date: Date) => Promise<void>;
};

export default function CalendarDatetimeMenu({
  currentDate,
  onCloseDateMenu,
  onSetDate,
  onUpdate = undefined,
}: Props) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(currentDate ?? new Date());
  const [selectedHour, setSelectedHour] = useState(
    currentDate ? String(currentDate.getHours()).padStart(2, '0') : ''
  );
  const [selectedMinute, setSelectedMinute] = useState(
    currentDate ? String(currentDate.getMinutes()).padStart(2, '0') : ''
  );

  const handleClickOutside = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const onClear = () => {
    setSelectedDate(undefined);
    setSelectedHour('');
    setSelectedMinute('');
  };

  const onSubmit = async () => {
    if (selectedDate) {
      const updatedDate = new Date(selectedDate);
      updatedDate.setHours(Number(selectedHour));
      updatedDate.setMinutes(Number(selectedMinute));
      onSetDate(updatedDate);
      if (onUpdate) {
        await onUpdate(updatedDate);
      }
      onCloseDateMenu();
      onClear();
    }
  };

  useEffect(() => {
    if (currentDate) {
      setSelectedDate(currentDate);
      setSelectedHour(String(currentDate.getHours()).padStart(2, '0'));
      setSelectedMinute(String(currentDate.getMinutes()).padStart(2, '0'));
    }
  }, []);

  return (
    <div className="border-2 border-solid border-border bg-white">
      <div className="flex max-h-[266.83px]">
        <div
          className="w-[236px] border-r border-b border-solid border-border"
          onClick={handleClickOutside}
        >
          <DatePicker
            selected={selectedDate}
            dateFormat="yyyy年MM月dd日"
            locale={'ja'}
            inline
            onChange={(date) => {
              if (date) {
                setSelectedDate(date);
              }
            }}
            calendarClassName="calendar-datetime-menu"
            renderCustomHeader={(params) => {
              return renderCustomHeader({
                date: params.date,
                customHeaderCount: params.customHeaderCount,
                decreaseMonth: params.decreaseMonth,
                increaseMonth: params.increaseMonth,
              });
            }}
          />
        </div>
        <div className="flex max-h-[266.83px] overflow-hidden border-b border-solid border-border">
          <div className="hidden-scrollbar flex h-[266.83px] w-[44px] flex-col items-center gap-2 overflow-y-auto border-r border-solid border-border bg-white">
            {Array.from({ length: 25 }, (_, i) => {
              const hour = String(i).padStart(2, '0');
              return (
                <div
                  key={`minutes-${i}`}
                  className={clsx(
                    'flex size-xl min-h-xl cursor-pointer items-center justify-center hover:rounded-[100px] hover:bg-[#EEEEEE]',
                    selectedHour === hour &&
                      'rounded-[100px] bg-primary text-white hover:bg-primary'
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedHour(hour);
                  }}
                >
                  {hour}
                </div>
              );
            })}
          </div>
          <div className="hidden-scrollbar flex h-[266.83px] w-[44px] flex-col items-center gap-2 overflow-y-auto bg-white">
            {Array.from({ length: 13 }, (_, i) => {
              const minutes = String(i * 5).padStart(2, '0');
              return (
                <div
                  key={`minutes-${i}`}
                  className={clsx(
                    'flex size-xl min-h-xl cursor-pointer items-center justify-center hover:rounded-[100px] hover:bg-[#EEEEEE]',
                    selectedMinute === minutes &&
                      'rounded-[100px] bg-primary text-white hover:bg-primary'
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMinute(minutes);
                  }}
                >
                  {minutes}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex h-[56px] items-center justify-end gap-1 bg-white px-3">
        <div className="w-[52px]">
          <BlockButton
            colorType="white"
            height={32}
            type="button"
            text="クリア"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
          />
        </div>
        <div className="w-xxl">
          <BlockButton
            height={32}
            type="button"
            text="決定"
            onClick={(e) => {
              e.stopPropagation();
              onSubmit();
            }}
          />
        </div>
      </div>
    </div>
  );
}
